import { useMemo, useRef, useState } from 'react';
import { FolderUp, UploadCloud } from 'lucide-react';
import {
  RESOURCE_CATEGORIES,
  requestResourceUploadUrl,
  uploadToSignedUrl,
  type ClientResource,
  type ResourceCategory,
} from '../../lib/clientsApi';
import { useCreateResource } from '../../hooks/useClients';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { useUploadLimit } from '../../hooks/useUploadLimit';
import { bytesToMb } from '../../lib/fileUpload';
import {
  DIRECTORY_INPUT_PROPS,
  filesFromDataTransfer,
  filesFromInput,
  type PickedTree,
} from '../../lib/folderUpload';
import { UploadProgressList } from '../ui/UploadProgress';
import type { TreeFile } from '../../lib/projectsApi';
import { EditWithAIButton } from '../ui/EditWithAIButton';
import {
  FolderView,
  buildTreeFromFiles,
  kindFromMime,
  type FlatFile,
} from '../resources/FolderView';
import { FilePreviewModal } from '../resources/FilePreviewModal';

const CATEGORY_LABELS: Record<ResourceCategory, string> = {
  CONTRACT: 'Contract',
  INVOICE: 'Factură',
  PROPOSAL: 'Propunere',
  NDA: 'NDA',
  OTHER: 'Altele',
};

// Stable folder order so the explorer always lists categories the same way.
const CATEGORY_ORDER = RESOURCE_CATEGORIES.map((c) => CATEGORY_LABELS[c]);

// Reverse map (folder label → category) so uploads land in the folder that is
// currently open in the Finder-style browser; at root files go to "Altele".
const CATEGORY_BY_LABEL: Record<string, ResourceCategory> = Object.fromEntries(
  RESOURCE_CATEGORIES.map((c) => [CATEGORY_LABELS[c], c]),
);

/**
 * Resources tab for the Client detail page (CLE-248).
 *
 * Uses the shared folder-view explorer (same layout/behaviour as Projects):
 * client resources are grouped into category folders, opened to reveal files,
 * with breadcrumb navigation and click-to-preview. Upload is drag & drop or
 * click, preserving the existing signed-S3 upload flow.
 */
export function ClientResourcesTab({
  clientId,
  clientName,
  resources,
}: {
  clientId: string;
  clientName?: string;
  resources: ClientResource[];
}) {
  const [folderPath, setFolderPath] = useState('');
  const category = CATEGORY_BY_LABEL[folderPath.split('/')[0]] ?? 'OTHER';
  // Per-file upload progress, rendered as rows above the folder browser.
  const { uploads, isUploading, run } = useUploadQueue();
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<TreeFile | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const uploadLimit = useUploadLimit();

  const createMutation = useCreateResource(clientId);

  const tree = useMemo(() => {
    const flat: FlatFile[] = resources.map((r) => ({
      id: r.id,
      name: r.name,
      category: CATEGORY_LABELS[r.category],
      kind: kindFromMime(null, r.fileType ? `x.${r.fileType.toLowerCase()}` : r.name),
      fileType: r.fileType,
      fileSize: r.fileSize,
      createdAt: r.uploadedAt,
      downloadUrl: r.s3Url,
    }));
    return buildTreeFromFiles(flat, {
      rootName: 'Resurse',
      categoryOrder: CATEGORY_ORDER,
      includeEmpty: true,
    });
  }, [resources]);

  async function handleFile(file: File) {
    // The progress row stays up for the whole flow — transfer, then the
    // server storing the file and the resource row being saved — so it only
    // clears once the file is really in the folder below.
    await run(file, async (onProgress) => {
      const contentType = file.type || 'application/octet-stream';
      const { uploadUrl, key } = await requestResourceUploadUrl(clientId, {
        fileName: file.name,
        category,
        contentType,
        fileSize: file.size,
      });
      await uploadToSignedUrl(uploadUrl, file, contentType, onProgress);
      await createMutation.mutateAsync({
        name: file.name,
        category,
        s3Key: key,
        fileType: (file.name.split('.').pop() ?? '').toUpperCase() || undefined,
        fileSize: file.size,
      });
    });
  }

  // Several files, or whole folders: there are no subfolders here, so every
  // file lands in the open category. A few transfers run side by side; one
  // failing does not stop the others.
  async function handlePicked({ files }: PickedTree) {
    if (inputRef.current) inputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
    if (files.length === 0) return;
    setError(null);
    const queue = files.map((f) => f.file);
    const failed: string[] = [];
    let lastError: string | null = null;
    async function worker() {
      for (let file = queue.shift(); file; file = queue.shift()) {
        try {
          await handleFile(file);
        } catch (err) {
          failed.push(file.name);
          lastError = err instanceof Error ? err.message : null;
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(3, queue.length) }, worker));
    if (failed.length === 1 && files.length === 1) {
      setError(lastError ?? 'Încărcarea a eșuat.');
    } else if (failed.length > 0) {
      setError(`Nu s-au putut încărca: ${failed.join(', ')}.${lastError ? ` ${lastError}` : ''}`);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    // Taken synchronously: the browser empties dataTransfer after the event.
    void filesFromDataTransfer(e.dataTransfer).then(handlePicked);
  }

  return (
    <div className="space-y-4">
      {/* Upload (drag & drop or click) — files land in the open folder */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-3">
          <EditWithAIButton
            variant="secondary"
            label="Încarcă prin AI"
            prompt={`Vreau să încarc un document (contract, factură, propunere sau NDA) pentru clientul${clientName ? ` „${clientName}”` : ''}. Te rog cere-mi fișierul și salvează-l în dosarul potrivit din resursele clientului.`}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        disabled={isUploading}
        className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 text-center transition-colors disabled:opacity-50 ${
          dragActive
            ? 'border-brand-500 bg-brand-50'
            : 'border-gray-300 bg-gray-50 hover:border-brand-400'
        }`}
      >
        <UploadCloud className="h-7 w-7 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">
          Trage și plasează fișiere sau foldere aici, sau apasă pentru a alege fișiere
        </span>
        <span className="text-xs text-gray-400">
          Salvat în {CATEGORY_LABELS[category]} · max. {bytesToMb(uploadLimit)} MB per fișier
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void handlePicked(filesFromInput(e.target.files));
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        {...DIRECTORY_INPUT_PROPS}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void handlePicked(filesFromInput(e.target.files));
        }}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => folderInputRef.current?.click()}
          disabled={isUploading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <FolderUp size={15} /> Încarcă folder
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <UploadProgressList uploads={uploads} />

      <FolderView
        root={tree}
        path={folderPath}
        onPathChange={setFolderPath}
        onPreview={setPreview}
        uploads={uploads}
      />

      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
